import React, { useEffect, useMemo, useState } from "react";
import { Card, Rate } from "antd";
import { FaHeart } from "react-icons/fa";
import secureLocalStorage from "react-secure-storage";
import "../style.css";
import { landCoverTypeLabels, priorityLabels } from "../utils/helperFunctions";

const PrintHelp = ({
  practice,
  userFavoritePractices = [],
  favOnchange,
  listView,
  selected,
  eligibleCREPs = [],
  crepColorMap,
  disableInteractions = false,
  rateBasisTable,
  rateBasisTitle,
  rateBasisValue,
  rateBasisNote,
}) => {
  const [cardClass, setCardClass] = useState("");
  const isLoggedIn = secureLocalStorage.getItem("token");

  const classNameSelector = () => {
    let myClassName = "practice-card";

    if (listView === "List") {
      myClassName += " list-view";
      if (selected) myClassName += " selected";
    } else if (listView === "Grid") {
      if (selected) myClassName += " selected";
      myClassName += " practice-card-vertical";
    } else {
      if (selected) myClassName += " selected";
      myClassName += " practice-card-horizontal";
    }
    return myClassName;
  };

  useEffect(() => {
    setCardClass(classNameSelector() + " print");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isVertical = !!cardClass?.includes("practice-card-vertical");
  const isListView = !!cardClass?.includes("list-view");

  const topRibbons = useMemo(() => {
    const out = [];

    if (practice?.isMostCommon) {
      out.push({ label: "Most Common", color: "#307d00", type: "most-common" });
    }

    if (Array.isArray(eligibleCREPs) && eligibleCREPs.length > 0) {
      eligibleCREPs.forEach((item) => {
        const name = item?.crepname;
        if (!name) return;

        const color =
          crepColorMap?.get?.(name) ||
          (typeof crepColorMap === "object" ? crepColorMap?.[name] : null) ||
          "#888";

        out.push({ label: name, color, type: "crep" });
      });
    }

    return out;
  }, [eligibleCREPs, practice?.isMostCommon, crepColorMap]);

  const practiceNumber = practice?.title?.split("-")?.[0]?.trim() || "";
  const practiceName = practice?.title?.split("-")?.[1]?.trim() || "";
  const isFavorited = userFavoritePractices.includes(practice?.title);

  // ----------------------------
  // Normalizers (fix "missing" sections)
  // ----------------------------

  const normalizedLandcovers = useMemo(() => {
    const raw =
      practice?.landcover ??
      practice?.landCover ??
      practice?.landcovers ??
      practice?.landCovers ??
      [];

    if (!Array.isArray(raw)) return [];

    // Allow strings or objects like { name: "cropland" }
    return raw
      .map((item) => (typeof item === "string" ? item : item?.name))
      .filter(Boolean)
      .filter((p) => p !== "timber");
  }, [practice]);

  const normalizedPriorities = useMemo(() => {
    const raw = practice?.priorities ?? practice?.prioritySelections ?? [];

    if (!Array.isArray(raw)) return [];

    // Allow strings or objects like { name: "wind" }
    return raw
      .map((item) => (typeof item === "string" ? item : item?.name))
      .filter(Boolean);
  }, [practice]);

  return (
    <Card
      className={cardClass}
      title={
        <>
          <p className="practice-number">
            {practiceNumber}
            <div style={{ float: "right" }}>
              {practice?.scoreLabel && (
                <span className={practice?.scoreClassName}>
                  {practice?.scoreLabel}
                </span>
              )}

              {!disableInteractions && isLoggedIn !== null && (
                <Rate
                  character={
                    <FaHeart
                      color={isFavorited ? "coral" : "lightgray"}
                      style={{ verticalAlign: "middle" }}
                    />
                  }
                  count={1}
                  value={isFavorited ? 1 : 0}
                  onChange={(val) =>
                    favOnchange?.(val, practice.title + "#" + practice.pdflink)
                  }
                />
              )}
            </div>
          </p>
          <p className="practice-title">{practiceName}</p>
        </>
      }
      headStyle={{
        backgroundColor: "#5D1725",
        color: "white",
      }}
    >
      <div
        className={`${
          isVertical ? "flag-container-vertical" : "flag-container-horizontal"
        } ${isListView ? "list" : ""}`}
      >
        {topRibbons.map((ribbon, index) => (
          <div
            key={`${ribbon.label}-${index}`}
            className={`${
              isVertical ? "practice-flag-vertical" : "practice-flag-horizontal"
            } ${
              ribbon.type === "most-common" ? "practice-flag-most-common" : ""
            }`}
            style={{
              ...(isVertical ? { top: `${index * 28}px` } : {}),
              backgroundColor: ribbon.color,
            }}
          >
            <p className="crep-flag">{ribbon.label}</p>
          </div>
        ))}
      </div>

      {normalizedLandcovers.length > 0 && (
        <div className="print step2_images">
          <b>Land Cover:</b>
          <ul>
            {normalizedLandcovers.map((p) => (
              <li key={p} title={landCoverTypeLabels[p]}>
                {landCoverTypeLabels[p] || p}
              </li>
            ))}
          </ul>
        </div>
      )}

      {normalizedPriorities.length > 0 && (
        <div className="print step3_images">
          <b>Priorities:</b>
          <ul>
            {normalizedPriorities.map((p) => (
              <li key={p} title={priorityLabels[p]}>
                {priorityLabels[p] || p}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default PrintHelp;
